import { createContext, useContext, useState, useEffect } from 'react';
import type { SubscriptionTier } from '@/types/subscription';

export type ImpersonatedRole = 'logged-out' | SubscriptionTier | null;

interface ImpersonationContextType {
    impersonatedRole: ImpersonatedRole;
    setImpersonatedRole: (role: ImpersonatedRole) => void;
    isImpersonating: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
    const [impersonatedRole, setImpersonatedRole] = useState<ImpersonatedRole>(() => {
        // Restore from localStorage if available
        const saved = localStorage.getItem('impersonatedRole');
        return (saved as ImpersonatedRole) || null;
    });

    useEffect(() => {
        if (impersonatedRole) {
            localStorage.setItem('impersonatedRole', impersonatedRole);
        } else {
            localStorage.removeItem('impersonatedRole');
        }
    }, [impersonatedRole]);

    return (
        <ImpersonationContext.Provider
            value={{
                impersonatedRole,
                setImpersonatedRole,
                isImpersonating: impersonatedRole !== null,
            }}
        >
            {children}
        </ImpersonationContext.Provider>
    );
}

export function useImpersonation() {
    const context = useContext(ImpersonationContext);
    if (context === undefined) {
        throw new Error('useImpersonation must be used within an ImpersonationProvider');
    }
    return context;
}
